/**
 * SceneMockup.tsx
 * Renders a desk scene image with the user's design perspective-warped
 * onto the white mat placeholder using CSS 3D transforms.
 *
 * Technique: CSS perspective transform via matrix3d()
 * The 4 corners of the mat in the scene image are mapped to the 4 corners
 * of the user's design image using a homography-like CSS matrix3d approach.
 */
import { useEffect, useRef, useMemo } from 'react'

export interface SceneCorners {
  /** top-left, top-right, bottom-right, bottom-left — as [x%, y%] of image dims */
  tl: [number, number]
  tr: [number, number]
  br: [number, number]
  bl: [number, number]
}

interface SceneMockupProps {
  /** Path to the scene background image */
  sceneImage: string
  /** The user's design as a data URL or image URL */
  designImage: string
  /** Corner coordinates as percentage of image dimensions */
  corners: SceneCorners
  /** Label shown in top-left badge */
  label: string
  /** Stitching color (CSS color string) */
  stitchingColor?: string
  /** Aspect ratio of the scene image (width/height) */
  aspectRatio?: number
  /** Optional rotation to apply to the design before warping (0, 90, 180, 270) */
  designRotation?: 0 | 90 | 180 | 270
}

/**
 * Compute the CSS matrix3d() transform that maps a unit square
 * [0,0] [1,0] [1,1] [0,1] to the 4 destination points.
 * Based on the algorithm from:
 * https://franklinta.com/2014/09/08/computing-css-matrix3d-transforms/
 */
function computeMatrix3d(
  w: number, h: number,
  tl: [number, number], tr: [number, number],
  br: [number, number], bl: [number, number]
): string {
  // Destination points in pixels
  const x0 = tl[0], y0 = tl[1]
  const x1 = tr[0], y1 = tr[1]
  const x2 = br[0], y2 = br[1]
  const x3 = bl[0], y3 = bl[1]

  // Source points: corners of the w×h image
  // We solve: [src] -> [dst] homography
  // Using the standard 8-point DLT algorithm simplified for a rectangle source

  const A = [
    [0, 0, 1, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 1, 0, 0],
    [w, 0, 1, 0, 0, 0, -w * x1, 0],
    [0, 0, 0, w, 0, 1, 0, -w * y1],
    [w, h, 1, 0, 0, 0, -w * x2, -h * x2],
    [0, 0, 0, w, h, 1, -w * y2, -h * y2],
    [0, h, 1, 0, 0, 0, 0, -h * x3],
    [0, 0, 0, 0, h, 1, 0, -h * y3],
  ]

  const b = [x0, y0, x1, y1, x2, y2, x3, y3]

  // Gaussian elimination
  const n = 8
  const M = A.map((row, i) => [...row, b[i]])

  for (let col = 0; col < n; col++) {
    // Find pivot
    let maxRow = col
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(M[row][col]) > Math.abs(M[maxRow][col])) maxRow = row
    }
    ;[M[col], M[maxRow]] = [M[maxRow], M[col]]

    for (let row = col + 1; row < n; row++) {
      const factor = M[row][col] / M[col][col]
      for (let k = col; k <= n; k++) {
        M[row][k] -= factor * M[col][k]
      }
    }
  }

  // Back substitution
  const h_vec = new Array(n).fill(0)
  for (let i = n - 1; i >= 0; i--) {
    h_vec[i] = M[i][n] / M[i][i]
    for (let j = i + 1; j < n; j++) {
      h_vec[i] -= (M[i][j] / M[i][i]) * h_vec[j]
    }
  }

  // h_vec = [h00, h01, h02, h10, h11, h12, h20, h21], h22 = 1
  const [h00, h01, h02, h10, h11, h12, h20, h21] = h_vec

  // CSS matrix3d is column-major 4x4
  // We map 2D homography to 3D CSS matrix
  const mat = [
    h00, h10, 0, h20,
    h01, h11, 0, h21,
    0,   0,   1, 0,
    h02, h12, 0, 1,
  ]

  return `matrix3d(${mat.map((v) => v.toFixed(8)).join(',')})`
}

export default function SceneMockup({
  sceneImage,
  designImage,
  corners,
  label,
  stitchingColor = '#7C3AED',
  aspectRatio = 16 / 9,
  designRotation = 0,
}: SceneMockupProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  // We use a canvas overlay approach:
  // 1. Draw the scene image as background
  // 2. Use canvas drawImage with perspective transform via offscreen canvas
  useEffect(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const sceneImg = new Image()
    const designImg = new Image()
    sceneImg.crossOrigin = 'anonymous'
    designImg.crossOrigin = 'anonymous'

    let sceneLoaded = false
    let designLoaded = false

    const draw = () => {
      if (!sceneLoaded || !designLoaded) return

      const W = canvas.width
      const H = canvas.height

      // Clear
      ctx.clearRect(0, 0, W, H)

      // Draw scene background
      ctx.drawImage(sceneImg, 0, 0, W, H)

      // Convert corner percentages to pixel coordinates
      const tl: [number, number] = [corners.tl[0] * W, corners.tl[1] * H]
      const tr: [number, number] = [corners.tr[0] * W, corners.tr[1] * H]
      const br: [number, number] = [corners.br[0] * W, corners.br[1] * H]
      const bl: [number, number] = [corners.bl[0] * W, corners.bl[1] * H]

      // Pre-rotate design if needed using an offscreen canvas
      let sourceImg: HTMLImageElement | HTMLCanvasElement = designImg
      if (designRotation !== 0) {
        const iw = designImg.naturalWidth
        const ih = designImg.naturalHeight
        const swap = designRotation === 90 || designRotation === 270
        const oc = document.createElement('canvas')
        oc.width = swap ? ih : iw
        oc.height = swap ? iw : ih
        const octx = oc.getContext('2d')!
        octx.translate(oc.width / 2, oc.height / 2)
        octx.rotate((designRotation * Math.PI) / 180)
        octx.drawImage(designImg, -iw / 2, -ih / 2)
        sourceImg = oc
      }

      // Draw design warped onto mat using canvas perspective transform
      // Canvas 2D doesn't natively support perspective warp, so we use
      // triangle subdivision for a good approximation
      drawPerspectiveImage(ctx, sourceImg as HTMLImageElement, tl, tr, br, bl)

      // Draw stitching border
      ctx.strokeStyle = stitchingColor
      ctx.lineWidth = Math.max(2, W * 0.003)
      ctx.setLineDash([W * 0.012, W * 0.006])
      ctx.beginPath()
      ctx.moveTo(tl[0], tl[1])
      ctx.lineTo(tr[0], tr[1])
      ctx.lineTo(br[0], br[1])
      ctx.lineTo(bl[0], bl[1])
      ctx.closePath()
      ctx.stroke()
      ctx.setLineDash([])
    }

    sceneImg.onload = () => { sceneLoaded = true; draw() }
    designImg.onload = () => { designLoaded = true; draw() }
    sceneImg.src = sceneImage
    designImg.src = designImage
  }, [sceneImage, designImage, corners, stitchingColor, designRotation])

  // Resize canvas when container resizes
  useEffect(() => {
    const container = containerRef.current
    const canvas = canvasRef.current
    if (!container || !canvas) return

    const observer = new ResizeObserver(() => {
      const w = container.clientWidth
      const h = Math.round(w / aspectRatio)
      canvas.width = w
      canvas.height = h
      // Trigger redraw by dispatching a custom event
      canvas.dispatchEvent(new Event('resize'))
    })

    observer.observe(container)
    return () => observer.disconnect()
  }, [aspectRatio])

  return (
    <div ref={containerRef} className="relative w-full rounded-2xl overflow-hidden">
      <canvas
        ref={canvasRef}
        className="w-full block"
        style={{ aspectRatio: `${aspectRatio}` }}
      />
      {/* Scene label badge */}
      {label && (
        <div className="absolute top-3 left-3 bg-brand-purple/90 backdrop-blur-sm text-white text-xs font-medium px-3 py-1 rounded-full">
          {label}
        </div>
      )}
    </div>
  )
}

/**
 * Draw an image warped into a quadrilateral using triangle subdivision.
 * Splits the quad into a grid of triangles for smooth perspective approximation.
 */
function drawPerspectiveImage(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  tl: [number, number],
  tr: [number, number],
  br: [number, number],
  bl: [number, number],
  divisions = 8
) {
  const iw = img.naturalWidth
  const ih = img.naturalHeight

  for (let row = 0; row < divisions; row++) {
    for (let col = 0; col < divisions; col++) {
      const u0 = col / divisions
      const u1 = (col + 1) / divisions
      const v0 = row / divisions
      const v1 = (row + 1) / divisions

      // Bilinear interpolation of destination corners
      const p00 = bilerp(tl, tr, bl, br, u0, v0)
      const p10 = bilerp(tl, tr, bl, br, u1, v0)
      const p01 = bilerp(tl, tr, bl, br, u0, v1)
      const p11 = bilerp(tl, tr, bl, br, u1, v1)

      // Source texture coordinates
      const s00: [number, number] = [u0 * iw, v0 * ih]
      const s10: [number, number] = [u1 * iw, v0 * ih]
      const s01: [number, number] = [u0 * iw, v1 * ih]
      const s11: [number, number] = [u1 * iw, v1 * ih]

      // Draw two triangles per cell
      drawTexturedTriangle(ctx, img, p00, p10, p11, s00, s10, s11)
      drawTexturedTriangle(ctx, img, p00, p11, p01, s00, s11, s01)
    }
  }
}

function bilerp(
  tl: [number, number], tr: [number, number],
  bl: [number, number], br: [number, number],
  u: number, v: number
): [number, number] {
  const top: [number, number] = [tl[0] + (tr[0] - tl[0]) * u, tl[1] + (tr[1] - tl[1]) * u]
  const bot: [number, number] = [bl[0] + (br[0] - bl[0]) * u, bl[1] + (br[1] - bl[1]) * u]
  return [top[0] + (bot[0] - top[0]) * v, top[1] + (bot[1] - top[1]) * v]
}

/**
 * Draw a textured triangle using canvas transform trick.
 * Maps source triangle (sx0,sy0),(sx1,sy1),(sx2,sy2) from img
 * to destination triangle (dx0,dy0),(dx1,dy1),(dx2,dy2).
 */
function drawTexturedTriangle(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  d0: [number, number], d1: [number, number], d2: [number, number],
  s0: [number, number], s1: [number, number], s2: [number, number]
) {
  ctx.save()

  // Clip to the destination triangle, expanded ~1.2px outward from its
  // centroid so adjacent triangles overlap and no anti-aliased seams show.
  const cx = (d0[0] + d1[0] + d2[0]) / 3
  const cy = (d0[1] + d1[1] + d2[1]) / 3
  const grow = (p: [number, number]): [number, number] => {
    const vx = p[0] - cx, vy = p[1] - cy
    const len = Math.hypot(vx, vy) || 1
    return [p[0] + (vx / len) * 1.2, p[1] + (vy / len) * 1.2]
  }
  const g0 = grow(d0), g1 = grow(d1), g2 = grow(d2)
  ctx.beginPath()
  ctx.moveTo(g0[0], g0[1])
  ctx.lineTo(g1[0], g1[1])
  ctx.lineTo(g2[0], g2[1])
  ctx.closePath()
  ctx.clip()

  // Compute affine transform from source to destination
  // [dx0, dx1, dx2]   [a, c, e]   [sx0, sx1, sx2]
  // [dy0, dy1, dy2] = [b, d, f] * [sy0, sy1, sy2]
  //                               [1,   1,   1  ]
  const [sx0, sy0] = s0
  const [sx1, sy1] = s1
  const [sx2, sy2] = s2
  const [dx0, dy0] = d0
  const [dx1, dy1] = d1
  const [dx2, dy2] = d2

  const denom = (sx0 * (sy1 - sy2) + sx1 * (sy2 - sy0) + sx2 * (sy0 - sy1))
  if (Math.abs(denom) < 1e-10) { ctx.restore(); return }

  const a = (dx0 * (sy1 - sy2) + dx1 * (sy2 - sy0) + dx2 * (sy0 - sy1)) / denom
  const b = (dy0 * (sy1 - sy2) + dy1 * (sy2 - sy0) + dy2 * (sy0 - sy1)) / denom
  const c = (sx0 * (dx1 - dx2) + sx1 * (dx2 - dx0) + sx2 * (dx0 - dx1)) / denom
  const d = (sx0 * (dy1 - dy2) + sx1 * (dy2 - dy0) + sx2 * (dy0 - dy1)) / denom
  const e = dx0 - a * sx0 - c * sy0
  const f = dy0 - b * sx0 - d * sy0

  ctx.transform(a, b, c, d, e, f)
  ctx.drawImage(img, 0, 0)

  ctx.restore()
}
